import { Draft, produce } from 'immer';
import { AnyObject, deepFreeze } from 'wired-utilities';
import { State } from 'zustand';
import { devtools } from 'zustand/middleware';
import create, { EqualityChecker, StateSelector, StateSliceListener, StoreApi, Subscribe } from 'zustand/vanilla';

export type SetState<S> = (set: (draft: Draft<S>) => unknown, replace?: boolean) => void;
export type GetState<S> = () => S;
export type ActionCreator<S> = (set: SetState<S>, get: GetState<S>) => AnyObject;
export type Store<S extends State, A extends ActionCreator<S>> = StoreActions<S> & StoreApi<S> & ReturnType<A>;
export interface StoreActions<S extends State> {
  $: <SelectorReturn = S>(selector: (state: S) => SelectorReturn) => SelectorReturn;
  select: <SelectorReturn = S>(selector: (state: S) => SelectorReturn) => SelectorReturn;
  produce: <T = S>(action: (state: Draft<T>) => void | T) => void;
  subscribeAndRun: Subscribe<S>;
  reset: () => void;
}
export interface StoreOptions {
  storeName?: string;
  warnAgainstAnonymousActions?: boolean;
  attachTo?: object;
}

export function createStore<S extends State, A extends ActionCreator<S>>(
  initialState: S,
  actionsCreator?: A,
  { storeName, warnAgainstAnonymousActions, attachTo }: StoreOptions = {},
): Store<S, A> {
  let actions: A;
  let config: any = immerMiddleware((set: SetState<S>, get: GetState<S>) => {
    actions = (actionsCreator?.(set, get) ?? {}) as A;
    return deepFreeze(initialState);
  }, warnAgainstAnonymousActions);
  if (storeName) {
    config = devtools(config, {
      name: storeName,
    });
  }

  let store = create(config);
  const select = <Selected = S>(selector: (state: S) => Selected) => selector(store.getState() as S);
  const builtInActions: StoreActions<S> = {
    select,
    $: select,
    // @ts-expect-error TypeScript gets confused with the templates here, aren't escape hatches wonderful?
    produce: (fn: (state: Draft<S>) => void | S) => {
      // @ts-expect-error: Immer + our custom types isn't happy
      const nextState = produce(store.getState(), fn);
      // @ts-expect-error TypeScript isn't aware of the the redux dev-tools-middleware, so it doesn't know about the third argument
      store.setState(nextState, true, fn.name);
    },
    reset: () => {
      // @ts-expect-error TypeScript isn't aware of the the redux dev-tools-middleware, so it doesn't know about the third argument
      store.setState(initialState, true, 'reset');
    },
    subscribeAndRun: <StateSlice>(
      listener: StateSliceListener<StateSlice>,
      selector?: StateSelector<S, StateSlice>,
      equalityFn?: EqualityChecker<StateSlice>,
    ) => {
      // @ts-expect-error: It's getting hairy in here
      const unsubscribe = store.subscribe(listener, selector, equalityFn);
      // @ts-expect-error: It's getting hairy in here
      const state = selector ? selector(store.getState()) : store.getState();
      // @ts-expect-error: It's getting hairy in here
      listener(state, state);
      return unsubscribe;
    },
  };

  store = Object.assign(store, builtInActions, actions!);
  const result = deepFreeze(store) as Store<S, A>;

  if (attachTo && storeName) {
    attachTo[storeName] = store;
  }

  return result;
}

// https://github.com/pmndrs/zustand#middleware
function immerMiddleware(config: Function, warnAgainstAnonymousActions?: boolean) {
  return (set: (state: unknown, ...args: any[]) => unknown, get: GetState<unknown>, api: StoreApi<object>) =>
    config(
      (fn: (state: unknown, ...args: any[]) => unknown) => {
        if (warnAgainstAnonymousActions && fn.name === '') {
          // eslint-disable-next-line no-console
          console.warn(
            'Unnamed function used as a producer. This results in a poor debugging experience, please use named functions (ex: function namedFunction() {}).',
            new Error().stack,
          );
        }
        return set(produce(fn), true, fn.name);
      },
      get,
      api,
    );
}
